/**
 * CustomLeaveTypesPanel — admin UI for the org's custom leave types.
 *
 * Lives at the top of /settings → Leave Policies. Lets HR admins
 * extend the standard request dropdown (Vacation / Personal / Sick
 * / Other) with their own categories — "Volunteer Day", "Birthday",
 * "Religious Observance", anything they need.
 *
 * Each custom type carries:
 *   - name             — display label in the request modal
 *   - description      — optional helper text
 *   - requires_reason  — whether the SPA forces a write-in reason
 *   - rollup_leave_type — the standard bucket this type counts as
 *                        for analytics (defaults to OTHER)
 *   - default_annual_days — used to seed LeaveBalance rows
 *   - color            — hex used for chips / calendar blocks
 *   - is_active        — flip off to retire without losing history
 *
 * Soft-delete only: the API does not hard-delete because historical
 * LeaveRequest rows still reference these ids. Inactive types stay
 * in the table greyed out so admins remember they exist.
 */
import { useEffect, useState } from "react";
import {
  EyeOff,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";

import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";

import {
  createCustomLeaveType,
  deleteCustomLeaveType,
  listCustomLeaveTypes,
  updateCustomLeaveType,
} from "@/api/leave";
import type {
  CustomLeaveType,
  CustomLeaveTypeInput,
  LeaveType,
} from "@/types/leave";

// Rollup options shown in the form. We deliberately limit to the
// four standard buckets — admins map their custom type to one of
// these for reporting cohesion, not to one of the legacy enum
// values.
const ROLLUP_OPTIONS: { value: LeaveType; label: string }[] = [
  { value: "PTO", label: "Vacation" },
  { value: "PERSONAL", label: "Personal" },
  { value: "SICK", label: "Sick" },
  { value: "OTHER", label: "Other" },
];

interface FormState extends CustomLeaveTypeInput {
  // Override to make required at form-level (the API accepts blank
  // for PATCH, but creating without a name is rejected).
  name: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  description: "",
  requires_reason: false,
  rollup_leave_type: "OTHER",
  default_annual_days: 0,
  color: "",
  is_active: true,
  sort_order: 0,
};

export function CustomLeaveTypesPanel() {
  const { addToast } = useToast();
  const [items, setItems] = useState<CustomLeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<CustomLeaveType | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      // include_inactive=true so admins can see retired types and
      // re-activate them if they were retired by mistake.
      const rows = await listCustomLeaveTypes(true);
      setItems(rows);
    } catch {
      addToast("error", "Failed to load custom leave types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (row: CustomLeaveType) => {
    setEditing(row);
    setForm({
      name: row.name,
      description: row.description ?? "",
      requires_reason: row.requires_reason,
      rollup_leave_type: row.rollup_leave_type,
      default_annual_days: row.default_annual_days,
      color: row.color ?? "",
      is_active: row.is_active,
      sort_order: row.sort_order,
    });
    setShowModal(true);
  };

  const close = () => {
    setShowModal(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const onSave = async () => {
    if (!form.name.trim()) {
      addToast("error", "Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload: CustomLeaveTypeInput = {
        name: form.name.trim(),
        description: form.description?.trim() || null,
        requires_reason: !!form.requires_reason,
        rollup_leave_type: form.rollup_leave_type,
        default_annual_days: Number(form.default_annual_days) || 0,
        color: form.color?.trim() || null,
        is_active: form.is_active ?? true,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editing) {
        await updateCustomLeaveType(editing.id, payload);
        addToast("success", "Leave type updated");
      } else {
        await createCustomLeaveType(payload);
        addToast("success", "Leave type added — it now shows up in the request menu");
      }
      close();
      await refresh();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Couldn't save leave type",
      );
    } finally {
      setSaving(false);
    }
  };

  const onRetire = async (row: CustomLeaveType) => {
    if (
      !window.confirm(
        `Retire "${row.name}"? It'll stop appearing in the request dropdown but historical requests will still show its name.`,
      )
    ) {
      return;
    }
    try {
      await deleteCustomLeaveType(row.id);
      addToast("success", `Retired "${row.name}"`);
      await refresh();
    } catch {
      addToast("error", "Couldn't retire that leave type");
    }
  };

  const onReactivate = async (row: CustomLeaveType) => {
    try {
      await updateCustomLeaveType(row.id, { is_active: true });
      addToast("success", `"${row.name}" is back in the request menu`);
      await refresh();
    } catch {
      addToast("error", "Couldn't reactivate that leave type");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom Leave Types</CardTitle>
        <Button size="sm" onClick={openNew}>
          <Plus className="h-4 w-4" /> Add type
        </Button>
      </CardHeader>

      <p className="text-sm text-text-muted mb-4">
        The standard request menu shows{" "}
        <strong>Vacation, Personal, Sick, Other</strong>. Add your own
        categories here — Volunteer Day, Birthday, Religious Observance —
        and they'll appear in every employee's time-off request modal.
        Mark "Requires reason" to force a write-in note when the type is
        selected.
      </p>

      {loading ? (
        <div className="text-sm text-text-muted py-6 text-center">
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-bentoSm border border-dashed border-border p-6 text-center text-sm text-text-muted">
          No custom leave types yet. Click <strong>Add type</strong> to
          create your first one.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-text-muted border-b border-border">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-left font-medium">Rolls up to</th>
                <th className="px-3 py-2 text-left font-medium">Reason?</th>
                <th className="px-3 py-2 text-right font-medium">
                  Default days
                </th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 w-[1%]" />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.id}
                  className={`border-b border-border/40 ${
                    !row.is_active ? "opacity-60" : ""
                  }`}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {row.color && (
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full"
                          style={{ background: row.color }}
                          aria-hidden
                        />
                      )}
                      <span className="font-medium text-text-primary">
                        {row.name}
                      </span>
                    </div>
                    {row.description && (
                      <div className="text-xs text-text-muted line-clamp-1">
                        {row.description}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-text-muted">
                    {ROLLUP_OPTIONS.find(
                      (o) => o.value === row.rollup_leave_type,
                    )?.label ?? row.rollup_leave_type}
                  </td>
                  <td className="px-3 py-2">
                    {row.requires_reason ? (
                      <Badge variant="info">Required</Badge>
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.default_annual_days || "—"}
                  </td>
                  <td className="px-3 py-2">
                    {row.is_active ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="default">Retired</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(row)}
                      aria-label={`Edit ${row.name}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {row.is_active ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onRetire(row)}
                        aria-label={`Retire ${row.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onReactivate(row)}
                        aria-label={`Reactivate ${row.name}`}
                      >
                        <EyeOff className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={close}
        title={editing ? "Edit leave type" : "New leave type"}
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            placeholder="e.g. Volunteer Day"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Description (optional)
            </label>
            <textarea
              value={form.description ?? ""}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={2}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
              placeholder="What this type covers — e.g. one paid day per year for community service"
            />
          </div>

          <Select
            label="Rolls up to (for reports)"
            value={form.rollup_leave_type ?? "OTHER"}
            onChange={(e) =>
              setForm({
                ...form,
                rollup_leave_type: e.target.value as LeaveType,
              })
            }
            options={ROLLUP_OPTIONS}
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Default annual days"
              type="number"
              step="0.5"
              min={0}
              value={form.default_annual_days ?? 0}
              onChange={(e) =>
                setForm({
                  ...form,
                  default_annual_days: Number(e.target.value),
                })
              }
            />
            <Input
              label="Sort order"
              type="number"
              value={form.sort_order ?? 0}
              onChange={(e) =>
                setForm({ ...form, sort_order: Number(e.target.value) })
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Colour (optional)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.color || "#1B18FF"}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="h-9 w-12 rounded-md border border-border cursor-pointer"
              />
              <Input
                value={form.color ?? ""}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                placeholder="#1B18FF"
                className="flex-1"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.requires_reason}
                onChange={(e) =>
                  setForm({ ...form, requires_reason: e.target.checked })
                }
                className="h-4 w-4 rounded border-border accent-accent"
              />
              <span className="text-sm text-text-primary">
                Requires a written reason
              </span>
            </label>
            <p className="text-xs text-text-muted pl-6">
              If on, the request modal forces employees to fill in a "Reason"
              field before they can submit.
            </p>
          </div>

          {editing && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
                className="h-4 w-4 rounded border-border accent-accent"
              />
              <span className="text-sm text-text-primary">Active</span>
            </label>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={onSave} isLoading={saving}>
              {editing ? "Save changes" : "Add type"}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

export default CustomLeaveTypesPanel;
